import LogoutButton from 'features/auth/logoutButton';
import BlurButton from 'features/blur/blurButton';
import PocToggle from 'features/poc/poc-toggle/pocToggle';
import { FC, useState } from 'react';
import SitePicker from './sitePicker';
import { RootState } from 'app/store';
import { useTypedSelector } from 'common/hooks/store';
import { NavLink } from 'react-router-dom';
import DropdownMenu from 'common/components/drop-downs/drop-down-menu';
import { useSelector } from 'react-redux';
import { debugToggleByType } from 'features/internal/internal-settings.slice';
import { ToggleType } from 'features/internal/internal.models';
import { selectIsSidebarOpen } from 'features/sidebar/sidebarSlice';
import { useAuth } from 'features/auth/hooks/useAuth';

const TopBar: FC = () => {
  const { isPocModeActive } = useTypedSelector(
    (state: RootState) => state.pocToggle
  );

  const { isSuperUser } = useAuth();

  const isNewSideBarEnabledDebugToggle = useSelector((state: RootState) =>
    debugToggleByType(state, ToggleType.DisplayNewSideBar)
  );

  const isSidebarOpenLegacy = useTypedSelector(
    (state: RootState) => state.sidebar.isSidebarOpen
  );

  const isSidebarOpen = useSelector(selectIsSidebarOpen);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <>
      <div
        className={`md:h-22 flex items-center w-full bg-white transition-all ${
          ((isNewSideBarEnabledDebugToggle?.enabled ?? false) &&
            isSidebarOpen) ||
          ((!isNewSideBarEnabledDebugToggle?.enabled ?? false) &&
            isSidebarOpenLegacy)
            ? 'md:pl-72'
            : 'md:pl-18'
        }`}
      >
        <div className="w-full p-2 md:p-4 flex justify-between items-center flex-wrap md:flex-nowrap">
          <SitePicker />
          <div className="flex items-center justify-between md:justify-end w-full mt-2 md:mt-0">
            <div className="flex items-center md:ml-3 md:mb-1">
              <DropdownMenu
                isDropdownOpen={isDropdownOpen}
                buttonLabel=""
                buttonClasses="rounded md:px-2 hover:bg-neutral-100 flex items-center"
                buttonIconClasses="ra-icons-generic-settings-1 text-primary-700 flex"
                dropdownMenuClasses="z-40 right-0 w-44 top-7 text-wrap"
                onDropdownClicked={() => setIsDropdownOpen(!isDropdownOpen)}
                onClickAway={() => setIsDropdownOpen(false)}
              >
                <NavLink
                  to={`site-setup`}
                  className={
                    'p-2.5 bg-white hover:bg-neutral-200 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all whitespace-nowrap text-sm'
                  }
                >
                  <span className="flex items-center">
                    <i className="ra-icons-generic-home-1 flex text-neutral-500 mr-2"></i>
                    <span className="leading-4">Site Setup</span>
                  </span>
                </NavLink>
                <NavLink
                  to={`setup-call-tracking`}
                  className={
                    'p-2.5 bg-white hover:bg-neutral-200 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all whitespace-nowrap text-sm'
                  }
                >
                  <span className="flex items-center">
                    <i className="ra-icons-generic-telephone flex text-neutral-500 mr-2"></i>
                    <span className="leading-4">Setup Call Tracking</span>
                  </span>
                </NavLink>

                {isPocModeActive ? (
                  <NavLink
                    to={`settings/integrations`}
                    className={
                      'p-2.5 bg-white hover:bg-neutral-200 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all whitespace-nowrap text-sm'
                    }
                  >
                    <span className="flex items-center">
                      <i className="ra-icons-generic-spanner-wrench text-neutral-500 flex mr-2"></i>
                      <span className="leading-4">Integrations</span>
                    </span>
                  </NavLink>
                ) : (
                  <></>
                )}

                <NavLink
                  to={`settings/webhooks`}
                  className={
                    'p-2.5 bg-white hover:bg-neutral-200 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all whitespace-nowrap text-sm'
                  }
                >
                  <span className="flex items-center">
                    <i className="ra-icons-generic-spanner-wrench text-neutral-500 flex mr-2"></i>
                    <span className="leading-4">Webhooks</span>
                  </span>
                </NavLink>

                {isPocModeActive ? (
                  <NavLink
                    to={`settings/exclusions`}
                    className={
                      'p-2.5 bg-white hover:bg-neutral-200 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all whitespace-nowrap text-sm'
                    }
                  >
                    <span className="flex items-center">
                      <i className="ra-icons-generic-spanner-wrench text-neutral-500 flex mr-2"></i>
                      <span className="leading-4">Exclusions</span>
                    </span>
                  </NavLink>
                ) : (
                  <></>
                )}

                {isPocModeActive ? (
                  <NavLink
                    to={`settings/segmentation-config`}
                    className={
                      'p-2.5 bg-white hover:bg-neutral-200 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all text-sm'
                    }
                  >
                    <span className="flex items-center">
                      <i className="ra-icons-generic-spanner-wrench text-neutral-500 flex mr-2 "></i>
                      <p className="leading-4">Segmentation</p>
                    </span>
                  </NavLink>
                ) : (
                  <></>
                )}

                {isPocModeActive ? (
                  <NavLink
                    to={`settings/data-science/tasks`}
                    className={
                      'p-2.5 bg-white hover:bg-neutral-200 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all whitespace-nowrap text-sm'
                    }
                  >
                    <span className="flex items-center">
                      <i className="ra-icons-generic-page-1 text-neutral-500 flex mr-2"></i>
                      <span className="leading-4">Data Science Tasks</span>
                    </span>
                  </NavLink>
                ) : (
                  <></>
                )}
                {isPocModeActive ? (
                  <NavLink
                    to={`settings/imports`}
                    className={
                      'p-2.5 bg-white hover:bg-neutral-200 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all whitespace-nowrap text-sm'
                    }
                  >
                    <span className="flex items-center">
                      <i className="ra-icons-generic-file text-neutral-500 flex mr-2"></i>
                      <span className="leading-4">Imports</span>
                    </span>
                  </NavLink>
                ) : (
                  <></>
                )}
                <hr className="border-t-1 border-neutral-200 " />

                {isSuperUser ? (
                  <NavLink
                    to={`settings/internal`}
                    className={
                      'p-2.5 bg-white hover:bg-neutral-200 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all whitespace-nowrap text-sm'
                    }
                  >
                    <span className="flex items-center">
                      <i className="ra-icons-generic-settings-1 text-neutral-500 flex mr-2"></i>
                      <span className="leading-4">Internal Settings</span>
                    </span>
                  </NavLink>
                ) : (
                  <></>
                )}

                <NavLink
                  to={`settings/general`}
                  className={
                    'p-2.5 bg-white hover:bg-neutral-200 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all whitespace-nowrap text-sm'
                  }
                >
                  <span className="flex items-center">
                    <i className="ra-icons-generic-settings-1 flex text-neutral-500 mr-2"></i>
                    <span className="leading-4">Settings</span>
                  </span>
                </NavLink>
                <hr className="border-t-1 border-neutral-200 " />
                <BlurButton buttonClasses="p-2.5 bg-white hover:bg-neutral-200 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all whitespace-nowrap text-sm text-left" />
                <LogoutButton
                  buttonLabel="Logout"
                  buttonClasses="p-2.5 bg-white hover:bg-neutral-200 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all whitespace-nowrap text-sm text-left"
                />
                <hr className="border-t-1 border-neutral-200 " />
                <NavLink
                  to={`help`}
                  className={
                    'p-2.5 bg-white hover:bg-neutral-200 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all whitespace-nowrap text-sm'
                  }
                >
                  <span className="flex items-center">
                    <i className="ra-icons-generic-question-1 flex text-neutral-500 mr-2"></i>
                    <span className="leading-4">Help</span>
                  </span>
                </NavLink>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TopBar;
